import { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import type { RefCallback } from 'react'
import styles from './Spotlight.module.css'

/**
 * The spotlight engine behind the product tour — ClickUp 86d3uknz0.
 *
 * Geometry only: find a target, frame it, and say where a card should sit beside
 * it. It knows nothing about onboarding, and it deliberately owns no modal
 * behaviour — the tour composes these with Radix Dialog so the locked shell has
 * exactly one focus trap, one scroll lock and one `aria-modal` contract instead
 * of two libraries both believing they own the document.
 *
 * Hand-rolled rather than driver.js/shepherd/joyride. Every tour library models
 * "target missing" as *skip the step*, and the one behaviour this feature must
 * have is the opposite: present the stop, drop the cutout (decision #11).
 * Fighting that is more code than owning this.
 */

/** Breathing room between the cutout edge and the target. */
const CUTOUT_PADDING = 6
/** Gap between the cutout and the card. */
const CARD_GAP = 16
/** The card's minimum distance from any viewport edge. */
const VIEWPORT_MARGIN = 16

export interface SpotlightRect {
  top: number
  left: number
  width: number
  height: number
}

/**
 * The box of the first candidate worth pointing at: one that has a layout box
 * with at least part of it inside the viewport.
 *
 * The second half is what makes the responsive fallback work without a media
 * query. Below 1024px the rail is `position: fixed` with `translateX(-100%)`, so
 * it is fully laid out — a plain "does it exist" check would happily frame a
 * rectangle hanging off the left edge of the screen.
 *
 * The winner's box is returned rather than re-read, because deciding "is it on
 * screen" and deciding "where is it" are the same measurement.
 */
function firstSpotlightBox(candidates: readonly HTMLElement[]): DOMRect | null {
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight
  for (const candidate of candidates) {
    const box = candidate.getBoundingClientRect()
    if (box.width <= 0 || box.height <= 0) continue
    if (box.right > 0 && box.bottom > 0 && box.left < viewportWidth && box.top < viewportHeight) {
      return box
    }
  }
  return null
}

/**
 * The box of the first candidate that is actually on screen — tracked live.
 *
 * Candidates arrive in fallback order from the anchor registry, so they are all
 * known to be *mounted*; this is where "and visible" is decided, because that is
 * a layout question and layout is only answerable after commit. It is also why
 * this is a subscription rather than a read during render: the whole answer
 * changes when the viewport crosses the 1024px breakpoint and the rail slides
 * off-canvas, and none of that is expressible as a pure function of props.
 *
 * Re-evaluated on everything that can move a target: the candidates changing,
 * any of them resizing, the viewport resizing (the mobile URL bar collapsing
 * fires this), and scroll anywhere — the rail and the sidebar are their own
 * scroll containers, so the `capture`-phase listener is what sees them move.
 *
 * Null means nothing in the chain is on screen. The stop still renders; it just
 * has nothing to frame, which is the only honest thing to do about it.
 */
export function useSpotlightTarget(candidates: readonly HTMLElement[]): SpotlightRect | null {
  const [rect, setRect] = useState<SpotlightRect | null>(null)

  const measure = useCallback(() => {
    const box = firstSpotlightBox(candidates)
    const next = box && {
      top: box.top - CUTOUT_PADDING,
      left: box.left - CUTOUT_PADDING,
      width: box.width + CUTOUT_PADDING * 2,
      height: box.height + CUTOUT_PADDING * 2,
    }
    // Same box in the same place: keep the identity, so a scroll event that
    // moved nothing does not re-render the card.
    setRect((current) => (current && next && sameRect(current, next) ? current : next))
  }, [candidates])

  // Bring the first candidate into view inside its own scroll container before
  // it is measured. Keyed on that candidate rather than folded into the
  // subscription below, so rebuilding the listeners does not scroll the shell
  // again. `nearest` keeps this to the smallest movement that works and never
  // touches document scroll, which Radix has locked anyway.
  const first = candidates[0]
  useLayoutEffect(() => {
    first?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
  }, [first])

  useLayoutEffect(() => {
    measure()

    const observer = new ResizeObserver(measure)
    for (const candidate of candidates) observer.observe(candidate)
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [candidates, measure])

  return rect
}

function sameRect(a: SpotlightRect, b: SpotlightRect): boolean {
  return a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height
}

export type SpotlightSide = 'right' | 'left' | 'bottom' | 'top' | 'center'

/**
 * Where an anchored card sits, as an inline style.
 *
 * `translate: none` cancels the centring `.card` carries by default. Without it
 * every anchored card would render half its own width and height up and to the
 * left of the position computed for it.
 */
export interface AnchoredCardStyle {
  top: number
  left: number
  translate: 'none'
}

function anchoredAt(position: { top: number; left: number }): AnchoredCardStyle {
  return { ...position, translate: 'none' }
}

export interface SpotlightPlacement {
  side: SpotlightSide
  /**
   * Undefined means "leave the card where its CSS puts it", which is centred —
   * see `.card` in Spotlight.module.css for why that case is expressed as the
   * absence of a placement rather than a computed one.
   */
  style: AnchoredCardStyle | undefined
  /**
   * Whether the cutout is still worth drawing.
   *
   * False when the card ends up sitting over its own target — only possible on
   * a viewport too small for the card to fit beside it at all (a tall stop on a
   * 320×568 phone), where centring is the last resort. A ring drawn under the
   * card that covers it is worse than no ring: it says "look here" while hiding
   * here. The stop's copy still names the target in words.
   */
  framesTarget: boolean
}

/** A measured box with no position yet — the card, before it is placed. */
export interface SpotlightCardSize {
  width: number
  height: number
}

/**
 * Where the card goes relative to the cutout.
 *
 * Right first, because every anchor this tour uses lives in the left-hand
 * chrome; then the other three sides; then centred, which is also what a null
 * rect gets. The clamp is applied on whichever axis is free — a card that fits
 * "on the right" can still overhang top or bottom once its height is known.
 *
 * The card's size is measured and passed in rather than restated here: `.card`
 * is `width: 360px` but also `max-width: calc(100vw - 32px)`, so a constant
 * copied from the stylesheet is wrong on exactly the narrow viewports where the
 * centred last resort — and with it `framesTarget` — actually fires.
 */
export function placeSpotlightCard(
  rect: SpotlightRect | null,
  card: SpotlightCardSize,
  viewport: { width: number; height: number },
): SpotlightPlacement {
  if (!rect || viewport.width === 0 || viewport.height === 0) {
    return { side: 'center', style: undefined, framesTarget: false }
  }

  // An unmeasured card has no basis for a placement, and treating it as
  // zero-sized is worse than having none: every side "fits", so it is anchored
  // flush against its target and then renders 360px of itself off the edge of
  // the screen. Centre it until the box is known — but keep the cutout, because
  // this lasts a frame and a spotlight that blinks reads far worse than a card
  // that is briefly in the middle.
  if (card.width === 0) {
    return { side: 'center', style: undefined, framesTarget: true }
  }

  const clampLeft = (left: number) =>
    Math.max(VIEWPORT_MARGIN, Math.min(left, viewport.width - card.width - VIEWPORT_MARGIN))
  const clampTop = (top: number) =>
    Math.max(VIEWPORT_MARGIN, Math.min(top, viewport.height - card.height - VIEWPORT_MARGIN))

  const right = rect.left + rect.width + CARD_GAP
  if (right + card.width + VIEWPORT_MARGIN <= viewport.width) {
    return { side: 'right', style: anchoredAt({ left: right, top: clampTop(rect.top) }), framesTarget: true }
  }

  const left = rect.left - CARD_GAP - card.width
  if (left >= VIEWPORT_MARGIN) {
    return { side: 'left', style: anchoredAt({ left, top: clampTop(rect.top) }), framesTarget: true }
  }

  const below = rect.top + rect.height + CARD_GAP
  if (below + card.height + VIEWPORT_MARGIN <= viewport.height) {
    return {
      side: 'bottom',
      style: anchoredAt({ left: clampLeft(rect.left), top: below }),
      framesTarget: true,
    }
  }

  const above = rect.top - CARD_GAP - card.height
  if (above >= VIEWPORT_MARGIN) {
    return { side: 'top', style: anchoredAt({ left: clampLeft(rect.left), top: above }), framesTarget: true }
  }

  // Nowhere to sit beside it: the card takes the middle of the screen. It only
  // keeps the cutout if the two do not actually collide there — on a viewport
  // short enough that they do, pointing at something the card is covering would
  // be worse than not pointing at all.
  return {
    side: 'center',
    style: undefined,
    framesTarget: !overlaps(rect, centredCard(card, viewport)),
  }
}

/**
 * Where a centred card lands, in viewport coordinates — the JS reading of
 * `.card`'s default position, and the only one. Exported so the tests that need
 * to know where an unanchored card sits ask this rather than re-deriving it.
 *
 * Only the overlap test needs it: the card itself is never positioned from this,
 * because CSS can centre a card whose size nobody has measured yet and this
 * cannot.
 */
export function centredCard(
  card: SpotlightCardSize,
  viewport: { width: number; height: number },
): SpotlightRect {
  return {
    left: (viewport.width - card.width) / 2,
    top: (viewport.height - card.height) / 2,
    width: card.width,
    height: card.height,
  }
}

function overlaps(a: SpotlightRect, b: SpotlightRect): boolean {
  return (
    a.left < b.left + b.width &&
    a.left + a.width > b.left &&
    a.top < b.top + b.height &&
    a.top + a.height > b.top
  )
}

/** Live viewport size — the only input placement needs beyond the rect. */
function useViewportSize() {
  const [size, setSize] = useState(() => ({
    width: typeof window === 'undefined' ? 0 : window.innerWidth,
    height: typeof window === 'undefined' ? 0 : window.innerHeight,
  }))
  useEffect(() => {
    // Same size in, same object out: the mobile URL bar collapsing fires resize
    // with an unchanged width, and a fresh object would re-place the card.
    const update = () =>
      setSize((current) =>
        current.width === window.innerWidth && current.height === window.innerHeight
          ? current
          : { width: window.innerWidth, height: window.innerHeight },
      )
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])
  return size
}

/**
 * Card placement, measured. Attach `cardRef` to the card: its box feeds the
 * clamps and the centred-overlap test, and re-measuring it on resize is what
 * keeps a stop with long copy from hanging off the bottom of a short viewport.
 *
 * A ref CALLBACK rather than a mount-time effect over a ref object, because the
 * card lives inside a Radix portal and does not exist on the commit that mounts
 * this hook's owner. An effect with `[]` deps would find `null`, bail, and never
 * run again — leaving the card unmeasured for its whole life. A callback runs
 * when the node actually attaches, whichever commit that is.
 */
export function useSpotlightCard(rect: SpotlightRect | null): {
  cardRef: RefCallback<HTMLElement>
  placement: SpotlightPlacement
} {
  const [card, setCard] = useState<SpotlightCardSize>({ width: 0, height: 0 })
  const viewport = useViewportSize()

  const cardRef = useCallback<RefCallback<HTMLElement>>((node) => {
    if (!node) return
    const update = () =>
      setCard((current) =>
        current.width === node.offsetWidth && current.height === node.offsetHeight
          ? current
          : { width: node.offsetWidth, height: node.offsetHeight },
      )
    update()
    const observer = new ResizeObserver(update)
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  return { cardRef, placement: placeSpotlightCard(rect, card, viewport) }
}

/**
 * The dimming layer with a hole in it.
 *
 * One huge `box-shadow` spread on a single element rather than four edge divs or
 * an SVG mask: it moves as one box between stops, and it cannot leave the
 * hairline seams at the corners that four abutting rectangles do.
 *
 * Rendered only when there IS a rect. A stop with nothing to point at gets the
 * backdrop's own dim instead and reads as a plain modal — never as a spotlight
 * on the top-left corner of the screen.
 */
export function SpotlightCutout({ rect }: { rect: SpotlightRect | null }) {
  if (!rect) return null
  return (
    <div
      className={styles.cutout}
      data-testid="product-tour-spotlight"
      style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }}
      aria-hidden
    />
  )
}
